import { API_BASE, getAccessToken } from "./client";

/**
 * Telemetría de uso de la PWA del jugador — mismo endpoint y misma tabla que
 * la app Android (`app_events`, migración 056), para poder comparar los dos
 * mundos en una sola consulta.
 *
 * Es el gemelo de Telemetry.kt, con dos diferencias que impone el navegador:
 *  · La pestaña se cierra sin avisar: se vacía el buffer en `pagehide` y en
 *    `visibilitychange` con `keepalive`, que sobrevive a la navegación
 *    (sendBeacon no sirve: no deja mandar el header de Authorization).
 *  · El canje de invitación ocurre SIN sesión y el endpoint pide auth, así
 *    que los eventos previos al login quedan en el buffer y se mandan con el
 *    primer flush que ya tenga token — así no se pierde el embudo de alta,
 *    que es justo lo que queremos medir.
 *
 * Fire-and-forget: cualquier error se traga. Nada de props sensibles ni de
 * alta cardinalidad (ids concretos, mails, nombres): rutas TEMPLATE y flags.
 */

type Props = Record<string, string | number | boolean>;

type Evento = {
  event: string;
  role: string | null;
  props?: Props | null;
  clientCreatedAtMs: number;
};

const LOTE = 12;
const MAX_BUFFER = 50;
const MAX_ESPERA_MS = 60_000;

let buffer: Evento[] = [];
let masViejoMs = 0;
let rol: string | null = "player";
let instalado = false;

/** Modo activo. Hoy la PWA es solo del jugador, pero queda parametrizado. */
export function setTelemetryRole(valor: string | null) {
  rol = valor;
}

function enviar(eventos: Evento[]) {
  if (eventos.length === 0) return;
  // Sin sesión el endpoint responde 401: los devolvemos al buffer para
  // mandarlos cuando el usuario termine de entrar.
  if (!getAccessToken()) {
    buffer = [...eventos, ...buffer].slice(-MAX_BUFFER);
    return;
  }
  // fetch directo, no apiFetch: necesitamos `keepalive` (el envío sobrevive
  // al cierre de la pestaña) y evitar que un 401 dispare el refresh de sesión
  // y el evento de "sesión expirada" — la medición no debe tocar el login.
  void fetch(`${API_BASE}/api/v1/telemetry/events`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    },
    body: JSON.stringify({ events: eventos }),
  }).catch(() => {
    /* medición: nunca molesta al usuario */
  });
}

export function track(event: string, props?: Props) {
  const ahora = Date.now();
  buffer.push({
    event,
    role: rol,
    props: props && Object.keys(props).length > 0 ? { ...props, instalado } : { instalado },
    clientCreatedAtMs: ahora,
  });
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
  if (buffer.length === 1) masViejoMs = ahora;
  if (buffer.length >= LOTE || ahora - masViejoMs > MAX_ESPERA_MS) {
    const salida = buffer;
    buffer = [];
    enviar(salida);
  }
}

/** Vacía lo pendiente ya. Best-effort. */
export function flushTelemetry() {
  if (buffer.length === 0) return;
  const salida = buffer;
  buffer = [];
  enviar(salida);
}

let iniciado = false;

/**
 * Arranca la medición. `standalone` distingue al que instaló la PWA del que
 * la usa en Safari suelto — la diferencia importa: compartir la tarjeta
 * funciona bien solo instalada.
 */
export function initTelemetry(standalone: boolean) {
  if (iniciado) return;
  iniciado = true;
  instalado = standalone;

  const ua = navigator.userAgent;
  const plataforma = /iPhone|iPad|iPod/.test(ua)
    ? "ios"
    : /Android/.test(ua)
      ? "android"
      : "desktop";
  track("session_start", { plataforma });

  // La pestaña puede morir sin aviso: `pagehide` cubre el cierre y el
  // back-forward cache; `visibilitychange` cubre el cambio de app en el celu.
  const vaciar = () => flushTelemetry();
  window.addEventListener("pagehide", vaciar);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") vaciar();
  });
}
